import { Link } from "@tanstack/react-router";
import PanelLeftIcon from "lucide-react/dist/esm/icons/panel-left";

import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";

import { NAV_GROUPS } from "./navigation";

interface AppSidebarProps {
  collapsed: boolean;
  onToggle: () => void;
  /** 未確認の致命アラート件数。ナビ上で気づけるようにする（原則 11-2）。 */
  criticalCount: number;
}

export function AppSidebar({ collapsed, onToggle, criticalCount }: AppSidebarProps) {
  return (
    <aside className="sticky top-0 flex h-screen flex-col border-r border-nav-line bg-nav text-nav-fg">
      <div
        className={cn(
          "flex h-12 shrink-0 items-center gap-2 border-b border-nav-line px-3",
          collapsed && "justify-center px-0"
        )}
      >
        <Link
          to="/"
          className="flex min-w-0 items-center gap-2"
          aria-label="Shopify Harness トップへ"
        >
          <span className="grid size-6 shrink-0 place-items-center rounded-md bg-brand text-[10px] font-semibold text-brand-fg">
            SH
          </span>
          {collapsed ? null : (
            <span className="truncate text-sm font-semibold tracking-tight">Shopify Harness</span>
          )}
        </Link>
      </div>

      <nav className="flex-1 overflow-y-auto py-3" aria-label="メインナビゲーション">
        {NAV_GROUPS.map((group) => (
          <div key={group.id} className="mb-4 px-2 last:mb-0">
            {collapsed ? (
              <div className="mx-auto mb-2 h-px w-6 bg-nav-line" />
            ) : (
              <p className="mb-1 px-2 text-2xs font-semibold tracking-wider text-nav-fg-muted uppercase">
                {group.label}
              </p>
            )}

            <ul className="grid gap-0.5">
              {group.items.map((item) => {
                const Icon = item.icon;
                const showBadge = item.to === "/alerts" && criticalCount > 0;

                const link = (
                  <Link
                    to={item.to}
                    activeOptions={{ exact: item.exact ?? false }}
                    className={cn(
                      "group relative flex h-8 items-center gap-2.5 rounded-md px-2 text-sm text-nav-fg-muted transition-colors hover:bg-nav-raised hover:text-nav-fg",
                      collapsed && "justify-center px-0"
                    )}
                    activeProps={{
                      className: cn(
                        "bg-nav-raised text-nav-fg font-semibold",
                        "[&>span[data-indicator]]:opacity-100"
                      )
                    }}
                  >
                    {/* 現在地はブランド色で示す。状態色は使わない（原則 5）。 */}
                    <span
                      data-indicator
                      className="absolute top-1.5 bottom-1.5 left-0 w-0.5 rounded-full bg-brand opacity-0"
                    />
                    <Icon className="size-4 shrink-0" aria-hidden="true" />
                    {collapsed ? null : <span className="truncate">{item.label}</span>}
                    {showBadge ? (
                      <span
                        className={cn(
                          "num ml-auto inline-flex h-4 min-w-4 items-center justify-center rounded-full bg-critical px-1 text-[10px] font-semibold text-white",
                          collapsed && "absolute top-0.5 right-1 ml-0"
                        )}
                      >
                        <span aria-hidden="true">{criticalCount}</span>
                        <span className="sr-only">件の未確認の致命アラート</span>
                      </span>
                    ) : null}
                  </Link>
                );

                return (
                  <li key={item.to}>
                    {collapsed ? (
                      <Tooltip>
                        <TooltipTrigger render={link} />
                        <TooltipContent side="right">
                          <p className="font-semibold">{item.label}</p>
                          <p className="text-xs opacity-80">{item.description}</p>
                        </TooltipContent>
                      </Tooltip>
                    ) : (
                      link
                    )}
                  </li>
                );
              })}
            </ul>
          </div>
        ))}
      </nav>

      <div className="shrink-0 border-t border-nav-line p-2">
        <button
          type="button"
          onClick={onToggle}
          aria-label={collapsed ? "サイドバーを開く" : "サイドバーを畳む"}
          className={cn(
            "flex h-8 w-full items-center gap-2.5 rounded-md px-2 text-sm text-nav-fg-muted transition-colors hover:bg-nav-raised hover:text-nav-fg",
            collapsed && "justify-center px-0"
          )}
        >
          <PanelLeftIcon className="size-4 shrink-0" aria-hidden="true" />
          {collapsed ? null : <span>畳む</span>}
        </button>
      </div>
    </aside>
  );
}
